/**
 * Controlled list of industries used for the `users.industry` enum and the
 * progressive-profile nudge UI. Keep slugs stable — they are stored in D1 and
 * referenced by analytics. Labels are user-facing and may be localized at the
 * component layer; do not rename slugs to fix labels.
 */
export const INDUSTRIES = [
	{ slug: "software-saas", label: "Software & SaaS" },
	{ slug: "fintech", label: "Fintech & Payments" },
	{ slug: "ai-ml", label: "AI & Machine Learning" },
	{ slug: "ecommerce", label: "E-commerce & Retail" },
	{ slug: "media-content", label: "Media & Content" },
	{ slug: "education", label: "Education & EdTech" },
	{ slug: "healthtech", label: "Health & HealthTech" },
	{ slug: "gaming", label: "Gaming" },
	{ slug: "marketing-agency", label: "Marketing & Agency" },
	{ slug: "design-creative", label: "Design & Creative" },
	{ slug: "consulting", label: "Consulting & Services" },
	{ slug: "logistics", label: "Logistics & Mobility" },
	{ slug: "web3-crypto", label: "Web3 & Crypto" },
	{ slug: "banking-finance", label: "Banking & Finance" },
	{ slug: "hr-recruitment", label: "HR & Recruitment" },
	{ slug: "legal", label: "Legal & Compliance" },
	// Non-tech fields added for the match domain guard (PR-9) so seekers in
	// these fields are inferable instead of falling out of the taxonomy and
	// losing all cross-domain match protection. Additive only — existing slugs
	// are stored in D1 (`users.industry`) and must never be renamed.
	{ slug: "records-archives", label: "Archives, Library & Records" },
	{ slug: "admin-office", label: "Administration & Office" },
	{ slug: "skilled-trades", label: "Skilled Trades" },
	{ slug: "other", label: "Other" },
] as const;

export type IndustrySlug = (typeof INDUSTRIES)[number]["slug"];

export const INDUSTRY_SLUGS: readonly IndustrySlug[] = INDUSTRIES.map(
	(i) => i.slug,
);

export function isIndustrySlug(value: unknown): value is IndustrySlug {
	return (
		typeof value === "string" &&
		(INDUSTRY_SLUGS as readonly string[]).includes(value)
	);
}

/**
 * Curated work-interest tags. Free-form interests are not allowed — the nudge
 * UI presents these as multi-select chips so we never collect noise like "fun"
 * or "money".
 */
export const WORK_INTERESTS = [
	"frontend",
	"backend",
	"fullstack",
	"mobile",
	"devops",
	"data-eng",
	"data-science",
	"ml-ai",
	"design",
	"product",
	"marketing",
	"sales",
	"customer-success",
	"writing",
	"ops",
	"qa",
	"security",
] as const;

export type WorkInterest = (typeof WORK_INTERESTS)[number];

export function isWorkInterest(value: unknown): value is WorkInterest {
	return (
		typeof value === "string" &&
		(WORK_INTERESTS as readonly string[]).includes(value)
	);
}
